import { DataModel } from "@toolpad/core/Crud";
import { useEffect, useState } from "react";
import { ISwapDto } from "../generated/api-client";
import { nameof } from "../utils/object.utils";
import MagicCrud, { MagicCrudFields } from "../components/MagicCrud";
import { AppClient } from "../services/client.service";
import { axiosInstance } from "../core/axios-interceptors";
import { getBaseUrl } from "../core/config";

type SwapDataModel = ISwapDto & DataModel;

type StatusOption = { label: string; value: string };

let cachedStatusOptions: StatusOption[] | null = null;
let statusOptionsPromise: Promise<StatusOption[]> | null = null;

function loadStatusOptions(): Promise<StatusOption[]> {
  if (!statusOptionsPromise) {
    statusOptionsPromise = AppClient.getInstance()
      .values(AppClient.version)
      .then((values) => {
        cachedStatusOptions = (values ?? []).map((v) => ({
          label: v,
          value: v,
        }));
        return cachedStatusOptions;
      });
  }
  return statusOptionsPromise;
}

export default function SwapsPage() {
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>(
    cachedStatusOptions ?? []
  );

  useEffect(() => {
    let cancelled = false;
    loadStatusOptions()
      .then((values) => {
        if (cancelled) {
          return;
        }
        setStatusOptions(values);
      })
      .catch((err) => {
        console.error("Failed to fetch swap status values:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const columns: MagicCrudFields<ISwapDto>[] = [
    {
      field: nameof<ISwapDto>("id"),
      type: "string",
      extraType: "guid",
      readOnly: true,
    },
    {
      field: nameof<ISwapDto>("batteryStationId"),
      type: "string",
      extraType: "guid",
      readOnly: true,
    },
    {
      field: nameof<ISwapDto>("batteryStationSerialNumber"),
      type: "string",
      readOnly: true,
    },
    {
      field: nameof<ISwapDto>("userId"),
      type: "string",
      extraType: "guid",
      readOnly: true,
    },
    {
      field: nameof<ISwapDto>("userEmail"),
      headerName: "email",
      type: "string",
      readOnly: true,
    },
    {
      field: nameof<ISwapDto>("groupId"),
      type: "string",
      extraType: "guid",
      readOnly: true,
    },
    {
      field: nameof<ISwapDto>("status"),
      type: "singleSelect",
      valueOptions: statusOptions,
      isRequired: true,
    },
    {
      field: nameof<ISwapDto>("statusTimeStamp"),
      type: "dateTime",
      readOnly: true,
    },
    {
      field: nameof<ISwapDto>("swapProcedureState"),
      type: "string",
      readOnly: true,
    },
    {
      field: nameof<ISwapDto>("providedBatteryId"),
      type: "string",
      extraType: "guid",
      readOnly: true,
    },
    {
      field: nameof<ISwapDto>("returnedBatteryId"),
      type: "string",
      extraType: "guid",
      readOnly: true,
    },
  ];

  return (
    <MagicCrud<SwapDataModel>
      name="swap"
      apiUrl="swaps"
      columns={columns}
      appUrl="/swaps"
      disable={{ create: true, delete: true }}
      showPermissionUrl="swaps"
      trasformDataForServer={(d) => ({ status: d.status })}
      defaultSort={[{ field: "statusTimeStamp", sort: "desc" }]}
      customGetOne={async (id) => {
        const response = await axiosInstance.get(
          `${getBaseUrl()}/swaps?$top=1&$filter=id eq ${id}`,
        );
        const item = response.data?.value?.[0];
        if (!item) {
          throw new Error();
        }
        return item as SwapDataModel;
      }}
    />
  );
}
